
import React from 'react';
import {
  Container, Card, Dropdown, Icon, Popup
} from 'semantic-ui-react';

import { inject } from 'mobx-react';

import moment from 'moment';
import _ from 'lodash';

import IconCard from './IconCard';
import ChartCard from './ChartCard';

import AreaChart from './AreaChart';
import PieChart from './PizzaChart';

import { ozzyInstance, percent } from '../lib/Util';
import { __, currentLocale } from '../lib/I18n';

import Main from '../layouts/Main';
import Loading from './Loading';

const MILLISECONDS_IN_A_DAY = 86400000;
const DAYS_BEFORE_SURVEY_EXPIRATION = 5;

/**
 * @param {Date} date
 * @param {'short'} [dateStyle]
 */
function formatDateToLocale(date, dateStyle = 'short') {
  return date.toLocaleDateString(currentLocale(), { dateStyle });
}

const confirmadoLabel = __('Casos confirmados');
const sintomaLabel = __('Sintomáticos no grupo familiar');
const contatoLabel = __('Exposição a casos confirmados');

function getAreaChartLabels() {
  const labels = [{
    label: confirmadoLabel,
    color: '#EB3322'
  }, {
    label: sintomaLabel,
    color: '#f3a540'
  }, {
    label: contatoLabel,
    color: '#E4D819'
  }];
  return labels;
}

/**
 * @param {{
 *  timestamp: string,
 *  numberOfAnswers: number,
 *  entitiesWithContact: number,
 *  entitiesWithSymptoms: number,
 *  entitiesWithCases: number,
 * }[]} surveysSummary
 * @param {string} timestamp
 * @param {number} ticks
 */
function getAreaChartData(surveysSummary, timestamp, ticks) {
  let areaChartData = [];
  let yMaxValue = 1;

  surveysSummary.forEach((day) => {
    areaChartData.push({
      date: formatDateToLocale(new Date(+day.timestamp)),
      numberAnswered: day.numberOfAnswers,
      timestamp: day.timestamp,
      [contatoLabel]: day.entitiesWithContact,
      [sintomaLabel]: day.entitiesWithSymptoms,
      [confirmadoLabel]: day.entitiesWithCases,
    });

    const sum = day.entitiesWithContact + day.entitiesWithSymptoms + day.entitiesWithCases;
    yMaxValue = Math.max(yMaxValue, Math.floor((sum * 1.2) / 4) * 4);
  });
  areaChartData.sort((a, b) => a.timestamp - b.timestamp);

  // Eliminar registros à frente do timestamp selecionado
  areaChartData = areaChartData.filter(value => value.timestamp <= timestamp);

  // Obter registros mais recentes
  areaChartData = areaChartData.slice(Math.max(areaChartData.length - ticks, 0));

  return { yMaxValue, areaChartData };
}

const formatRecords = (value) => {
  if (value == 1) return __('1 registro');
  return __('%s registros', value);
};

function getTicksForAreaChart() {
  const width = window.innerWidth;
  if (width > 1200) return 8;

  const multiplier = (Math.max(width - 400, 400) / 1000);
  return Math.min(Math.round(multiplier * 8), 8);
}

const querySurveySubmissionDates = `query DrixSurveySubmissionDates ($subscriptionId: Int!) {
  surveySubmissionDates(input: { subscription_id: $subscriptionId })
}`;

const querySurveysSummary = `query DrixSurveysSummary (
  $subscriptionId: Int!, $startDate: String!, $endDate: String!, $excludeEntities: [Int!]
) {
  surveysSummary(input: {
    subscription_id: $subscriptionId, start: $startDate, end: $endDate, exclude_entities: $excludeEntities
  }) {
    timestamp
    numberOfAnswers
    safeEntities
    entitiesWithContact
    entitiesWithSymptoms
    entitiesWithCases
  }
}`;

@inject('store')
export default class CoronaVirusCard extends React.Component {
  constructor(props) {
    super(props);

    const { groups } = this.props;
    this.state = {
      ticksForAreaChart: 0,
      /** @type {string[]} */
      selectedGroups: groups.map(g => g.name),
      /** @type {string[]} */
      loadedGroups: [],
      error: null,
      loading: true,
      timestamp: Date.now(),
      /** @type {string[]} */
      surveySubmissionDates: [],
      /**
       * @type {{
       *  timestamp: string,
       *  numberOfAnswers: number,
       *  safeEntities: number,
       *  entitiesWithContact: number,
       *  entitiesWithSymptoms: number,
       *  entitiesWithCases: number,
       * }[]}
       */
      surveysSummary: [],
    };
  }

  async componentDidMount() {
    this.updateAreaChartTicks();
    window.addEventListener('resize', () => this.updateAreaChartTicks());

    try {
      const ozzyResponse = await ozzyInstance(this.props.store.user.accessToken).post('', {
        query: querySurveySubmissionDates,
        variables: {
          subscriptionId: this.props.subscriptionId,
        },
      });

      const { surveySubmissionDates } = ozzyResponse.data.data;

      this.setState({
        surveySubmissionDates,
        timestamp: Math.max(...surveySubmissionDates.map(Number)),
      }, async () => {
        await this.fetchAnswers();
      });
    } catch (error) {
      console.error('Failed to get survey dates', error);
      this.setState({ loading: false, error: error.message });
    }
  }

  updateAreaChartTicks = () => {
    const { timestamp, ticksForAreaChart } = this.state;
    const ticks = getTicksForAreaChart(timestamp);
    if (ticks != ticksForAreaChart) {
      this.setState({ ticksForAreaChart: ticks });
    }
  }


  dropdownCheckbox = selected => ((selected) ? ({ name: 'check square', style: { fontWeight: 'bold', color: '#0080ff' } }) : ({ name: 'square', style: { color: '#cacdcf' } }));

  /**
   * @returns {(string | number)[]}
   */
  getEntitiesNotSelected() {
    const { selectedGroups } = this.state;
    const { groups } = this.props;

    const selectedEntities = _.flatten(
      groups.filter(g => selectedGroups.includes(g.name)).map(g => g.entities.nodes.map(e => e.id))
    );

    const entitiesNotSelected = _.uniq(_.flatten(
      groups.filter(g => !selectedGroups.includes(g.name)).map(g => g.entities.nodes.map(e => e.id))
    ).filter(entityId => !selectedEntities.includes(entityId)));

    return entitiesNotSelected;
  }

  async fetchAnswers() {
    const { timestamp, selectedGroups } = this.state;
    const { store, subscriptionId } = this.props;

    store.cache = store.cache || {};

    // Request 8 weeks of history
    const millisecondsIn10Weeks = 8 * 7 * MILLISECONDS_IN_A_DAY;
    const startDate = moment(new Date(Math.min(
      new Date().getTime() - MILLISECONDS_IN_A_DAY,
      timestamp - millisecondsIn10Weeks - MILLISECONDS_IN_A_DAY
    ))).format('Y-MM-DD');

    const endDate = moment(new Date(timestamp + MILLISECONDS_IN_A_DAY)).format('Y-MM-DD');

    const query = querySurveysSummary;

    const variables = {
      subscriptionId,
      startDate,
      endDate,
      excludeEntities: this.getEntitiesNotSelected(),
    };

    const cacheKey = query + '\n\n' + JSON.stringify(variables);

    let ozzyResponse;

    if (store.cache[cacheKey]) {
      ozzyResponse = store.cache[cacheKey];
    } else {
      try {
        ozzyResponse = await ozzyInstance(store.user.accessToken).post('', {
          query,
          variables,
        });
        store.cache[cacheKey] = ozzyResponse;
      } catch (error) {
        console.error('Failed to get surveys summary', error);
        this.setState({ loading: false, error: error.message });
        return;
      }
    }

    /**
     * @type {{
     *  timestamp: string,
     *  numberOfAnswers: number,
     *  safeEntities: number,
     *  entitiesWithContact: number,
     *  entitiesWithSymptoms: number,
     *  entitiesWithCases: number,
     * }[]}
     */
    const surveysSummary = ozzyResponse.data.data.surveysSummary;

    this.setState({
      surveysSummary,
      loading: false,
      loadedGroups: selectedGroups,
    });
  }

  render() {
    const { organization } = this.props;

    /**
     * @type {{
     *  id: number,
     *  name: string,
     *  entities: {
     *    nodes: {
     *      id: number,
     *    }[],
     *  },
     * }[]}
     */
    const groups = this.props.groups;

    const { numberOfStudents } = organization;
    const {
      loading, error, timestamp, surveySubmissionDates, ticksForAreaChart, selectedGroups,
      surveysSummary, loadedGroups,
    } = this.state;

    if (loading) {
      return (
        <Main className="container-loading">
          <Loading width={40} height={40} />
        </Main>
      );
    }

    if (error) {
      return (
        <Main className="container-root">
          <Container fluid>
            <div style={{ justifyContent: 'center' }}>
              <span>Erro na query: </span>
              <span>{error}</span>
            </div>
          </Container>
        </Main>
      );
    }

    const { yMaxValue, areaChartData } = getAreaChartData(surveysSummary, timestamp, ticksForAreaChart);

    let selectedDay = surveysSummary.find(day => `${day.timestamp}` === `${timestamp}`) || surveysSummary[0];

    if (!selectedDay) {
      selectedDay = {
        timestamp,
        entitiesWithCases: 0,
        entitiesWithContact: 0,
        entitiesWithSymptoms: 0,
        numberOfAnswers: 0,
        safeEntities: 0,
      };
    }

    return (
      <Container fluid>
        <Card.Group>
          <Card className="icon-card-container">
            <Card.Content>
              <div style={{ margin: 'auto', color: '#444' }}>
                <Dropdown
                  text={__('Grupos Selecionados: %s', selectedGroups.length)}
                  onClose={() => {
                    if (_.isEqual(loadedGroups, selectedGroups)) return;
                    this.setState({ loading: true }, () => this.fetchAnswers());
                  }}
                >
                  <Dropdown.Menu>
                    {groups.map(g => (
                      <Dropdown.Item
                        key={g.id}
                        onClick={(e) => {
                          let newSelectedGroups = [];
                          if (selectedGroups.includes(g.name)) {
                            newSelectedGroups = selectedGroups.filter(e => e !== g.name);
                          } else {
                            newSelectedGroups = [...selectedGroups];
                            newSelectedGroups.push(g.name);
                          }
                          this.setState({ selectedGroups: newSelectedGroups });
                          return e.stopPropagation();
                        }}
                        onKeyPress={() => { }}
                      >
                        <Icon {...this.dropdownCheckbox(selectedGroups.includes(g.name))} />
                        {g.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <Popup
                content={__('descricao filtro grupos')}
                trigger={<Icon className="infoIcon" style={{ position: 'absolute', right: 5, top: 5 }} as="i" name="info circle" />}
                basic
                wide="very"
              />
            </Card.Content>
          </Card>
          <Card className="icon-card-container">
            <Card.Content>
              <div style={{ margin: 'auto', color: '#444' }}>
                {__('Data selecionada:')}
                <Dropdown
                  placeholder={__('Selecione a data')}
                  scrolling
                  value={timestamp}
                  selection
                  options={surveySubmissionDates.map(timestamp => {
                    return {
                      key: +timestamp,
                      value: +timestamp,
                      text: formatDateToLocale(new Date(+timestamp)),
                    };
                  })}
                  style={{ marginLeft: 15 }}
                  onChange={(e, { value }) => {
                    this.setState({
                      timestamp: value,
                      loading: true,
                    }, () => {
                      this.fetchAnswers();
                    });
                  }}
                />
              </div>

              <Popup
                content={__('descricao filtro data')}
                trigger={<Icon className="infoIcon" style={{ position: 'absolute', right: 5, top: 5 }} as="i" name="info circle" />}
                basic
                wide="very"
              />
            </Card.Content>
          </Card>
        </Card.Group>

        <Card.Group>
          <IconCard
            iconClass="blue"
            title={__('Registros')}
            subtitle={`${__('%s de %s', selectedDay.numberOfAnswers, numberOfStudents)}`}
            iconName="poll"
            description={__('descricao card respostas')}
          />
          <IconCard
            iconClass="red"
            title={__('Casos confirmados')}
            subtitle={`${formatRecords(selectedDay.entitiesWithCases)} (${
              percent(selectedDay.entitiesWithCases, numberOfStudents)
            })`}
            iconName="users medical"
            description={__('descricao card casos')}
          />
          <IconCard
            iconClass="orange"
            title={__('Sintomáticos no grupo familiar')}
            subtitle={`${formatRecords(selectedDay.entitiesWithSymptoms)} (${
              percent(selectedDay.entitiesWithSymptoms, numberOfStudents)
            })`}
            iconName="users"
            description={__('descricao card sintomas')}
          />
          <IconCard
            iconClass="yellow"
            title={__('Exposição a casos confirmados')}
            subtitle={`${formatRecords(selectedDay.entitiesWithContact)} (${
              percent(selectedDay.entitiesWithContact, numberOfStudents)
            })`}
            iconName="users"
            description={__('descricao card contatos')}
          />
        </Card.Group>
        <Card.Group>
          <ChartCard
            className="chart-area"
            title={__('Histórico de dados')}
            description={__('descricao grafico de area', areaChartData.length)}
          >
            <AreaChart
              data={areaChartData}
              labels={getAreaChartLabels()}
              yMaxValue={yMaxValue}
              numberOfStudents={numberOfStudents}
            />
          </ChartCard>
          <ChartCard
            className="chart-pie"
            title={__('Percentual de grupos familiares seguros')}
            subtitle={timestamp ? __(
              'Período de pesquisa: de %s a %s',
              formatDateToLocale(new Date(timestamp), 'short'),
              formatDateToLocale(new Date(Math.min(
                new Date().getTime(),
                timestamp + DAYS_BEFORE_SURVEY_EXPIRATION * MILLISECONDS_IN_A_DAY
              )), 'short')
            ) : null}
            description={__('descricao grafico de pizza')}
          >
            <PieChart
              confirmed={selectedDay.entitiesWithCases}
              contact={selectedDay.entitiesWithContact}
              safe={selectedDay.safeEntities}
              numberOfStudents={numberOfStudents}
              symptoms={selectedDay.entitiesWithSymptoms}
            />
          </ChartCard>
        </Card.Group>
      </Container>
    );
  }
}
